import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatMoneyCents, gcShort } from '../lib/format';
import { AddHistoricalPcoFlow } from './AddHistoricalPcoFlow';
import { HelpTip } from './HelpTip';
import { useToast } from './ToastProvider';
import {
  BucketCreateFields,
  BucketRemoveButton,
  bodyForRow,
  blankPendingRow,
  isBlankNewRow,
  parseAmountCents,
  type BucketRow,
} from './pco/BucketRowEditor';
import type { PcoBillingType, PcoWriteRequest, SettingsPco } from '@coffman-tm/shared';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';

/**
 * Create-new-PARENT-PCO modal. This is the only form in the app that
 * creates a row in the `pcos` table — those rows are the change-order
 * scopes Hoffman has authorized (PCO 2, PCO 4A, PCO 20, etc.). Each
 * carries the MOD# from Coffman's contract paperwork, the FWA# from
 * Hoffman's authorization, and the approved money.
 *
 * Weekly billings (sub-PCOs, rendered as "PCO {parent}.{n}") are NOT
 * created here — they auto-generate as `weekly_rollups` rows when work
 * flows in for a given (parent PCO, week) tuple. There is no UI to
 * manually create one and there shouldn't be.
 *
 * Two-step: a CHOOSER, then the form.
 * -----------------------------------
 * Client ruling 2026-07-28: only a handful of PCOs actually bill through
 * weekly T&M rollups in the portal (002 / 004 / 009 / 016 / 020 on 25132);
 * the rest are lump-sum, priced outside and hard-entered here purely so the
 * PCO Log carries the funding picture. That distinction is now first-class
 * (`pcos.billing_type`), so creating a PCO starts by naming which world it
 * belongs to.
 *
 * BOTH billing-type branches land in the SAME form — the difference is the
 * `billing_type` the row is created with, not the fields. The one-motion
 * hard-price flow (bcff9a0: type the amount, mark Pending/Approved, add the
 * MOD, done) IS this form, so forking it into two near-identical dialogs would
 * just be two places for validation to drift. Callers that already know the
 * type (`billingType` prop) skip the chooser entirely.
 *
 * A THIRD option (client, 2026-07-30) sits below a divider: **Historical
 * Data** — onboarding a job whose billing predates the portal. It is not a
 * billing type and not this form; it hands off to `AddHistoricalPcoFlow`,
 * which either picks an existing PCO or minimally creates one and then takes a
 * grid of already-billed rows. It lives behind THIS chooser rather than a
 * fourth button somewhere because "add a PCO" is the gesture the PM already
 * has, and the entry-point lock below only knows about one of them.
 *
 * Used by:
 *   - SideRail's "New PCO" button         → chooser
 *   - PCO Log's "Add PCO" button          → chooser
 *   - Settings' unmapped-activity-code quick-create → no chooser; an
 *     activity code by definition tickets weekly rollup work, so that path
 *     passes billingType="weekly_rollup" directly.
 *
 * On success the caller decides what to do with the new PCO (navigate to its
 * detail page, prefill an activity-code remap, etc.) via `onCreated`.
 *
 * Status is not picked here — a new PCO always POSTs `status: 'open'`
 * (funding buckets, finding #8). Its displayed status is derived from the
 * money picture thereafter (a Pending bucket -> "Proposed", an Approved one
 * with a MOD -> "Approved"); paid/void are set later from the PCO page.
 * Approved date and the no-cost toggle live in "Edit details" — neither is
 * something a PM knows at the moment of creation.
 */
export function NewPcoModal({
  open,
  onClose,
  onCreated,
  initial,
  billingType,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (pco: SettingsPco) => void | Promise<void>;
  /** Defaults for the form. Used by the unknowns flow to prefill. */
  initial?: Partial<PcoWriteRequest>;
  /**
   * Skip the chooser and create this billing type. Omit to let the PM pick.
   */
  billingType?: PcoBillingType;
}) {
  const { token, me } = useAuth();
  const toast = useToast();
  // 'historical' is not a billing type — it's the pre-portal catch-up branch,
  // which renders its own flow instead of this form.
  const [chosen, setChosen] = useState<PcoBillingType | 'historical' | null>(null);
  const [num, setNum] = useState('');
  const [desc, setDesc] = useState('');
  const [comments, setComments] = useState('');
  const [rows, setRows] = useState<BucketRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset form to caller-provided initials every time we open. `rows` starts
  // with one blank Pending row — ready for the PM to type an amount straight
  // away — prefilled from `initial.amount_proposed_cents` when the caller
  // supplies one (no current caller does, but PcoWriteRequest carries the
  // field, so honor it rather than silently drop it).
  useEffect(() => {
    if (!open) return;
    setChosen(billingType ?? null);
    setNum(initial?.pco_number ?? '');
    setDesc(initial?.description ?? '');
    setComments(initial?.comments ?? '');
    const seed = blankPendingRow();
    setRows([
      initial?.amount_proposed_cents != null
        ? { ...seed, amountDollars: (initial.amount_proposed_cents / 100).toFixed(2) }
        : seed,
    ]);
    setBusy(false);
    setErr(null);
  }, [open, initial, billingType]);

  useBodyScrollLock(open);

  // Esc to close. Skipped on the historical branch — that flow owns its own
  // Esc so a grid with unsaved rows gets the discard confirm instead of
  // vanishing.
  useEffect(() => {
    if (!open || chosen === 'historical') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose, chosen]);

  if (!open) return null;

  if (chosen === null) {
    return <BillingTypeChooser onClose={onClose} onPick={setChosen} />;
  }

  if (chosen === 'historical') {
    return (
      <AddHistoricalPcoFlow
        onClose={onClose}
        onBack={() => setChosen(null)}
        onCreated={onCreated}
      />
    );
  }

  const setRow = (key: string, patch: Partial<BucketRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addBucket = () => setRows((rs) => [...rs, blankPendingRow()]);
  const removeRow = (key: string) => setRows((rs) => rs.filter((r) => r.key !== key));

  const approvedSum = rows
    .filter((r) => r.approved)
    .reduce((a, r) => a + parseAmountCents(r.amountDollars), 0);
  const pendingSum = rows
    .filter((r) => !r.approved)
    .reduce((a, r) => a + parseAmountCents(r.amountDollars), 0);

  const submit = async () => {
    if (!token) return;
    if (!num.trim() || !desc.trim()) {
      setErr('PCO number and description are required.');
      return;
    }
    for (const r of rows) {
      if (r.amountDollars.trim() !== '' && !Number.isFinite(Number(r.amountDollars))) {
        setErr('Bucket amounts must be numbers.');
        return;
      }
      // No approvedDate guard: this form renders BucketCreateFields without
      // showFundedDate, so the field can't be typed into here. The historical
      // flow, which does show it, carries the DATE_RE check.
    }
    setBusy(true);
    setErr(null);
    try {
      const body: PcoWriteRequest = {
        pco_number: num.trim(),
        description: desc.trim(),
        // Bucket-first from creation on — the money lives in funding_tranches
        // (created below), not this legacy field. Mirrors EditPcoModal
        // retiring amount_proposed_cents on every save.
        amount_proposed_cents: null,
        // Always 'open' — status is derived from money after creation, and
        // paid/void are set later from the PCO page (funding buckets #8).
        status: 'open',
        billing_type: chosen,
        comments: comments.trim() || null,
      };
      const created = await api.settings.createPco(token, body);

      // Create a funding_tranches row per non-blank bucket. If one fails to
      // save, the PCO itself still exists (created above) — surface a warning
      // toast and let the PM finish it from "Edit details" rather than
      // blocking the create that already succeeded (same recovery shape as
      // the unmapped-code auto-map flow).
      let bucketsSaved = 0;
      let bucketError = false;
      for (const r of rows) {
        if (isBlankNewRow(r)) continue;
        try {
          await api.pcos.createTranche(token, created.id, bodyForRow(r));
          bucketsSaved += 1;
        } catch (te) {
          bucketError = true;
          toast.push({
            variant: 'warning',
            title: `PCO ${num.trim()} created, but a bucket didn't save`,
            body: `Add it from "Edit details" on the PCO page. (${
              te instanceof Error ? te.message : 'unknown error'
            })`,
          });
          break;
        }
      }

      // Post-save nudge, deliberately AFTER the fact rather than a hint under
      // the fields: a bucket normally IS created before Hoffman executes it,
      // so telling the PM up front that MOD/FWA can wait is noise on every
      // single create. Reminding them once, when they've actually left one
      // blank, lands. Non-blocking — the PCO is already saved.
      const missingRefs = rows.some(
        (r) => !isBlankNewRow(r) && !r.mod.trim() && !r.fwa.trim(),
      );
      if (!bucketError && bucketsSaved > 0 && missingRefs) {
        toast.push({
          variant: 'info',
          title: 'Reminder',
          body: 'Fill in the MOD / FWA when you receive it.',
        });
      }

      await onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create PCO');
    } finally {
      setBusy(false);
    }
  };

  const bucketLabel = rows.length > 1 ? 'Buckets' : 'Bucket';
  // A single bucket's chip/amount/remove cluster lives in the card head; a
  // multi-bucket list gives each row its own. Captured so the narrowing holds.
  const soleBucket = rows.length === 1 ? rows[0] : undefined;

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Create new parent PCO"
    >
      <div
        className="new-pco-modal pco-form-labels"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pdf-modal-head">
          <div style={{ minWidth: 0 }}>
            <div
              className="pdf-modal-title"
              style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'visible' }}
            >
              New parent PCO
              {/* The old standing intro paragraph lives here now — it's
                  orientation the PM needs once, not a permanent block of copy
                  above the first field. */}
              <HelpTip title="Creating a parent PCO" learnMore="pcos-and-funding">
                A parent PCO is one change-order scope. Give it a number and
                description, then enter the bucket below — everything else
                (weekly billings, the PCO Log row) follows automatically. Leave a
                bucket blank if there's no number yet and add it later from
                "Edit details"; negative amounts are fine (a deduct that takes
                money back). Comments show as a (?) tooltip on the PCO Log row.
              </HelpTip>
            </div>
          </div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="new-pco-body">
          <div className="pco-card">
            <div className="pco-card-head">
              <span className="pco-card-title">PCO details</span>
            </div>
            <div className="pco-card-body">
              <div className="new-pco-row">
                <div className="new-pco-field">
                  <label className="field-label">PCO #</label>
                  <input
                    className="field-input mono"
                    value={num}
                    onChange={(e) => setNum(e.target.value)}
                    autoFocus
                    disabled={busy}
                    placeholder="002"
                  />
                </div>
                <div className="new-pco-field">
                  <label className="field-label">Description</label>
                  <input
                    className="field-input"
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    disabled={busy}
                    placeholder="C60A03 Rock Hammering Allowance"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ===== Funding bucket(s) ===== */}
          {/* With one bucket its remove button lives in the card head; with
              several, each row carries its own beside its label. */}
          <div className="pco-card">
            <div className="pco-card-head">
              <span className="pco-card-title">
                {bucketLabel}
                <HelpTip title="What a funding bucket is" learnMore="pcos-and-funding">
                  A bucket is one {gcShort(me)} approval against this scope — the
                  same thing "Edit details" manages later. The amount on its own
                  is a perfectly good <strong>Pending</strong> bucket; if{' '}
                  {gcShort(me)} has already executed the change order, set the
                  Status to <strong>Funded</strong> and add the MOD number.
                </HelpTip>
              </span>
              {soleBucket && (
                <BucketRemoveButton
                  index={0}
                  busy={busy}
                  onRemove={() => removeRow(soleBucket.key)}
                />
              )}
            </div>
            <div className="pco-card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {rows.map((r, i) => (
                  <div
                    key={r.key}
                    style={
                      i > 0
                        ? { borderTop: '1px solid var(--rule)', paddingTop: 12 }
                        : undefined
                    }
                  >
                    {rows.length > 1 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <span className="field-label">Bucket {i + 1}</span>
                        <BucketRemoveButton
                          index={i}
                          busy={busy}
                          onRemove={() => removeRow(r.key)}
                        />
                      </div>
                    )}
                    <BucketCreateFields
                      row={r}
                      index={i}
                      busy={busy}
                      onChange={(patch) => setRow(r.key, patch)}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="pco-card-foot">
              <button
                type="button"
                className="btn btn-ghost btn-compact"
                onClick={addBucket}
                disabled={busy}
              >
                + Add Bucket
              </button>
              {(approvedSum !== 0 || pendingSum !== 0) && (
                <div
                  className="mono"
                  style={{ fontSize: 12.5, color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {approvedSum !== 0 && `Approved ${formatMoneyCents(approvedSum)}`}
                  {approvedSum !== 0 && pendingSum !== 0 && '  ·  '}
                  {pendingSum !== 0 && `Pending ${formatMoneyCents(pendingSum)}`}
                </div>
              )}
            </div>
          </div>

          <div className="pco-card">
            <div className="pco-card-head">
              <span className="pco-card-title">Comments</span>
            </div>
            <div className="pco-card-body">
              <input
                className="field-input"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                disabled={busy}
                placeholder="optional"
              />
            </div>
          </div>
          {err && (
            <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>
          )}
          <div className="new-pco-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-cta" onClick={submit} disabled={busy}>
              {busy ? 'Creating…' : 'Create parent PCO'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Step 1 — which billing world does this PCO live in? Two options, no
 * default: picking wrong is a real (if recoverable, via Edit details)
 * misfile, so the PM reads both lines rather than tabbing past a pre-selected
 * radio. Both paths continue into the same form.
 *
 * The third option is a different KIND of answer, so it sits below a rule
 * rather than as a peer in the list: the first two say how a PCO will bill
 * from now on; "Historical Data" says this PCO was already being billed
 * before the portal existed, and takes the whole history in one grid. A
 * historical PCO still bills weekly-rollup or lump-sum going forward — that
 * choice lives inside that flow.
 *
 * Both lead-in questions were deleted 2026-08-05 — the copy lock in
 * `NewPcoModal.entrypoints.test.ts` bans them by exact string, comments
 * included, so they are not repeated here. Three titled cards with sublines
 * are already the question, and the rule keeps the grouping the second
 * question used to carry in words.
 */
function BillingTypeChooser({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (t: PcoBillingType | 'historical') => void;
}) {
  return (
    <div
      className="pdf-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Add a PCO"
    >
      <div className="new-pco-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-head">
          <div className="pdf-modal-title">Add PCO</div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="new-pco-body">
          {/* No lead-in questions (2026-08-05). Each option's own subline says
              what it is, and the rule below does the grouping the second
              question used to do in words. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ChooserOption
              title="T&amp;M Weekly Rollup"
              subline="Weekly foreman-PDF billing through the portal."
              autoFocus
              onClick={() => onPick('weekly_rollup')}
            />
            <ChooserOption
              title="Lump-Sum"
              subline="Priced outside, tracked on the PCO Log."
              onClick={() => onPick('lump_sum')}
            />
          </div>
          <div
            style={{
              height: 1,
              background: 'var(--rule)',
              margin: '2px 0',
            }}
            aria-hidden
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ChooserOption
              title="Historical Data"
              subline="Enter what was already billed, straight from your PCO Log."
              onClick={() => onPick('historical')}
            />
          </div>
          <div className="new-pco-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChooserOption({
  title,
  subline,
  onClick,
  autoFocus,
}: {
  title: string;
  subline: string;
  onClick: () => void;
  /** First option takes focus so the chooser is keyboard-operable on open. */
  autoFocus?: boolean;
}) {
  return (
    <button
      type="button"
      className="pco-type-option"
      onClick={onClick}
      autoFocus={autoFocus}
    >
      <span className="pco-type-option-title">{title}</span>
      <span className="pco-type-option-sub">{subline}</span>
    </button>
  );
}
